import type { ParsedValue, ParsedClasses, EditorBreakpoint, PropertyValue } from './types';
import { isPropertyValue } from './types';

/**
 * Helper to safely get a PropertyValue from ParsedClasses.
 * Returns an empty object if the property doesn't exist or isn't a PropertyValue.
 */
function getPropertyValue(parsed: ParsedClasses, property: string): PropertyValue {
  const value = parsed[property];
  return isPropertyValue(value) ? value : {};
}

/**
 * Compare two ParsedValues for semantic equality.
 *
 * Note: This intentionally ignores `breakpoint` and `normalizedValue` when comparing.
 * - `breakpoint` is excluded because this function is used for diff-based logic
 *   (e.g., comparing a base value to an lg value to determine if they're equivalent)
 * - `normalizedValue` is excluded because it's derived from `value` and `type`
 *
 * Values are considered equal if they have the same property, value, type, and modifier.
 */
export function valuesAreEqual(a?: ParsedValue, b?: ParsedValue): boolean {
  if (!a || !b) return a === b;
  return a.property === b.property && a.value === b.value && a.type === b.type && a.modifier === b.modifier;
}

export function getEffectiveValue({
  parsed,
  property,
  breakpoint,
}: {
  parsed: ParsedClasses;
  property: string;
  breakpoint: EditorBreakpoint;
}): ParsedValue | undefined {
  const propertyValue = getPropertyValue(parsed, property);
  if (!propertyValue.base && !propertyValue.lg) {
    return undefined;
  }

  if (breakpoint === 'lg') {
    return propertyValue.lg ?? propertyValue.base;
  }

  return propertyValue.base;
}

export function getEffectiveValues({
  parsed,
  breakpoint,
}: {
  parsed: ParsedClasses;
  breakpoint: EditorBreakpoint;
}): Record<string, ParsedValue> {
  const result: Record<string, ParsedValue> = {};

  for (const [property] of Object.entries(parsed)) {
    if (property !== 'unknown') {
      const effective = getEffectiveValue({ parsed, property, breakpoint });
      if (effective) {
        result[property] = effective;
      }
    }
  }

  return result;
}

/**
 * Set a value in ParsedClasses. Property and breakpoint are extracted from the value.
 * Implements diff-based logic: if setting lg to same as base, removes lg override.
 */
export function setValue(parsed: ParsedClasses, value: ParsedValue): ParsedClasses {
  const { property, breakpoint } = value;
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  if (breakpoint === 'base') {
    newParsed[property] = { ...currentPropertyValue, base: value };
  } else if (valuesAreEqual(value, currentPropertyValue.base)) {
    // Setting lg to same as base: remove lg override (diff-based)
    if (currentPropertyValue.base) {
      newParsed[property] = { base: currentPropertyValue.base };
    } else {
      delete newParsed[property];
    }
  } else {
    newParsed[property] = { ...currentPropertyValue, lg: value };
  }

  return newParsed;
}

/**
 * Set a value with "preserve desktop" behavior.
 * When setting base and there's no lg override, the old base value is promoted to lg.
 * This preserves the desktop experience when editing mobile.
 *
 * Use this for properties where values are mutually exclusive modes (e.g., display: flex/grid/hidden).
 *
 * @example
 * // Start: grid (base only - both mobile and desktop show grid)
 * // Set base to hidden
 * // Result: hidden lg:grid (mobile hidden, desktop still shows grid)
 */
export function setValuePreservingDesktop(parsed: ParsedClasses, value: ParsedValue): ParsedClasses {
  const { property, breakpoint } = value;
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  if (breakpoint === 'base') {
    // If there's a base value but no lg override, promote old base to lg first
    if (currentPropertyValue.base && !currentPropertyValue.lg && !valuesAreEqual(currentPropertyValue.base, value)) {
      const promotedLg: ParsedValue = { ...currentPropertyValue.base, breakpoint: 'lg' };
      newParsed[property] = { base: value, lg: promotedLg };
    } else {
      newParsed[property] = { ...currentPropertyValue, base: value };
    }
  } else if (valuesAreEqual(value, currentPropertyValue.base)) {
    // Setting lg to same as base: remove lg override (diff-based)
    if (currentPropertyValue.base) {
      newParsed[property] = { base: currentPropertyValue.base };
    } else {
      delete newParsed[property];
    }
  } else {
    newParsed[property] = { ...currentPropertyValue, lg: value };
  }

  return newParsed;
}

/**
 * Clear a value at a specific breakpoint.
 * - Clearing base: removes base value, keeps lg if present
 * - Clearing lg: removes lg override, falls back to base
 */
export function clearValue(parsed: ParsedClasses, property: string, breakpoint: EditorBreakpoint): ParsedClasses {
  const newParsed = { ...parsed };
  const currentPropertyValue = getPropertyValue(parsed, property);

  if (breakpoint === 'base') {
    // Clear base, but preserve lg if it exists
    if (currentPropertyValue.lg) {
      newParsed[property] = { lg: currentPropertyValue.lg };
    } else {
      delete newParsed[property];
    }
  } else if (currentPropertyValue.base) {
    // Clear lg, fall back to base
    newParsed[property] = { base: currentPropertyValue.base };
  } else {
    delete newParsed[property];
  }

  return newParsed;
}
